import { onMounted, onBeforeUnmount, ref, reactive, computed, watch } from 'vue'
import { throttle } from 'lodash'
import { isLargeViewPort, isMobile } from '@remote/utils/base'

interface ILayout {
  /** 当前布局每行展示个数 */
  col: number
  width: number | string
  minWidth?: number | string
  maxWidth?: number | string
}
export function useCinemaLayout (orientation: 'portrait' | 'landscape' = 'portrait') {
  const layout: ILayout = reactive({ width: 0, col: 0 })
  
  const onLayoutChange = throttle(() => {
    const vw = document.documentElement.clientWidth
    // 侧边栏宽度
    const asideWidth = isLargeViewPort() ? 240 : 0
    const containerPadding = isLargeViewPort() ? 32 : 16
    const cardPadding = isLargeViewPort() ? 10 : 8
    const containerWidth = vw - asideWidth - ((containerPadding - cardPadding) * 2)
    const screen = {
      // xs: 1100,
      // sm: 1400,
      md: 1100,
      lg: 1400,
      xl: 1720,
    }

    // 两种计算方式:
    //  1. 根据不同屏幕宽度，设定每行展示固定个数，卡片宽度随行个数而变化
    //  2. 根据不同屏幕宽度，设定卡片固定宽度（或最小宽度和最大宽度）, 行个数随卡片宽度而变化
    if (orientation === 'portrait') {
      if (!isLargeViewPort()) {
        // 满足1100屏幕时一行展示8个的情况下，卡片宽度w为: (1100 - 0 - (16 - 8) * 2) / 9 < w < (1100 - 0 - (16 - 8) * 2) / 8，即(120.66667, 135.75)
        const itemWidth = 105 + cardPadding * 2
        const maxCount = Math.floor(containerWidth / itemWidth)
        // 移动端保持至少3个卡片
        const col = Math.max(maxCount, 3)
        layout.col = col
        layout.width = `${100 / col}%`
      } else {
        let col = 8
        // vw 必定 > screen.md
        if (vw < screen.lg) {
          // [1100, 1400) 每行展示6个卡片
          col = 6
        } else if (vw < screen.xl) {
          // [1400, 1920) 每行展示7个卡片
          col = 7
        } else {
          // 1920及以上每行展示8个以上卡片
          const itemWidth = 184 + 20
          const maxCount = Math.floor(containerWidth / itemWidth)
          // 至少展示8个卡片
          col = Math.max(maxCount, 8)
        }
        layout.col = col
        layout.width = `${100 / col}%`
      }
    } else {
      if (!isLargeViewPort()) {
        const itemWidth = 202 + cardPadding * 2
        const maxCount = Math.floor(containerWidth / itemWidth)
        // 移动端保持至少3个卡片
        const col = Math.max(maxCount, 3)
        layout.col = col
        layout.width = `${100 / col}%`
      } else {
        let col = 6
        // vw 必定 > screen.md
        if (vw < screen.lg) {
          // [1100, 1400) 每行展示6个卡片
          col = 4
        } else if (vw < screen.xl) {
          // [1400, 1920) 每行展示7个卡片
          col = 5
        } else {
          // 1920及以上每行展示8个以上卡片
          const itemWidth = 252 + 20
          const maxCount = Math.floor(containerWidth / itemWidth)
          // 至少展示8个卡片
          col = Math.max(maxCount, 6)
        }
        layout.col = col
        layout.width = `${100 / col}%`
      }
    }
  }, 120, { leading: true, trailing: true })
  
  onMounted(() => {
    onLayoutChange()
    window.addEventListener('resize', onLayoutChange)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onLayoutChange)
  })
  
  return {
    layout,
  }
}

interface IPagation {
  /** 分页器是否展示，当页数大于1时展示 */
  visible: boolean
  /** 当前页码，从1开始 */
  page: number
  /** 总页码 */
  total: number
}
export function useCinemaCollectionPagation (id: string, nfos: API_NFO.NFO[], orientation: 'portrait' | 'landscape') {
  const list = ref(nfos)
  const isLargeView = ref(isLargeViewPort())
  const { layout } = useCinemaLayout(orientation)
  const cinemaLayout = computed(() => {
    if (isLargeView.value) return layout
    return { col: list.value.length, width: '' }
  })
  const total = computed(() => {
    if (!layout.col) return 1
    return Math.ceil(list.value.length / layout.col)
  })
  const pagation: IPagation = reactive({
    id,
    visible: isMobile() ? false : total.value > 1,
    page: 1,
    total: total.value,
  })

  const canprev = computed(() => pagation.page > 1)
  const cannext = computed(() => pagation.page < total.value)

  const prevPage = throttle(() => {
    if (!canprev.value) return
    pagation.page -= 1
  }, 500, { leading: true, trailing: false })

  const nextPage = throttle(() => {
    if (!cannext.value) return
    pagation.page += 1
  }, 500, { leading: true, trailing: false })

  function refresh () {
    const pageSize = layout.col
    const total = Math.ceil(list.value.length / pageSize)
    pagation.visible = isMobile() ? false : total > 1
    pagation.total = total
    if (total && pagation.page > total) pagation.page = total
  }

  watch(() => list.value.length, () => {
    refresh()
  })
  watch(() => layout.col, () => {
    refresh()
  })

  function updateList (nfos: API_NFO.NFO[]) {
    list.value = nfos
  }

  const onLayoutChange = throttle(() => {
    const current = isLargeViewPort()
    if (isLargeView.value !== current) {
      isLargeView.value = current
      refresh()
    }
  }, 120, { leading: true, trailing: true })

  onMounted(() => {
    onLayoutChange()
    window.addEventListener('resize', onLayoutChange)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onLayoutChange)
  })
  
  return {
    layout: cinemaLayout,
    pagation,
    canprev,
    prevPage,
    cannext,
    nextPage,
    updateList,
  }
}
